/**
 * Serial entry on the phone (procurement.md §2 Receive): scan a barcode with the camera through the BarcodeDetector
 * API where the browser has it (Chrome on Android), and always allow typing (iOS Safari and desktops have no
 * detector). Nothing is guessed: a scan only fills the field, and the person still saves the receipt.
 */
import { Button, Input } from '@patchbay/ui'
import { ScanLine, X } from 'lucide-react'
import * as React from 'react'

interface DetectedBarcode {
  rawValue: string
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>
}
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike

const FORMATS = ['code_128', 'code_39', 'code_93', 'qr_code', 'data_matrix', 'ean_13', 'upc_a', 'itf']

function detectorCtor(): BarcodeDetectorCtor | null {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
  return typeof ctor === 'function' ? ctor : null
}

export function canScan(): boolean {
  return Boolean(detectorCtor() && globalThis.navigator?.mediaDevices?.getUserMedia)
}

/** Strip label prefixes printed beside the barcode ("S/N:", "SN ", "MAC:") and surrounding space. */
export function cleanScan(raw: string): string {
  return raw
    .trim()
    .replace(/^(?:s\/?n|serial(?:\s*no\.?)?|mac)\s*[:#]?\s*/i, '')
    .trim()
}

/** Looks like a MAC address (12 hex, optional separators). Used to route a scan into the MAC field. */
export function looksLikeMac(value: string): boolean {
  return /^(?:[0-9a-f]{2}[:-]?){5}[0-9a-f]{2}$/i.test(value.trim())
}

function Scanner({ onResult, onClose }: { onResult: (value: string) => void; onClose: (error?: string) => void }) {
  const video = React.useRef<HTMLVideoElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: start once per open; the callbacks are stable enough
  React.useEffect(() => {
    const Ctor = detectorCtor()
    if (!Ctor) {
      onClose('This browser cannot scan barcodes; type the serial')
      return
    }
    let stream: MediaStream | null = null
    let timer: ReturnType<typeof setInterval> | undefined
    let stopped = false
    const stop = () => {
      stopped = true
      if (timer) clearInterval(timer)
      for (const t of stream?.getTracks() ?? []) t.stop()
    }
    const detector = new Ctor({ formats: FORMATS })
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(async (s) => {
        stream = s
        if (stopped) return stop()
        const el = video.current
        if (!el) return
        el.srcObject = s
        await el.play().catch(() => undefined)
        let busy = false
        timer = setInterval(async () => {
          if (busy || stopped || !video.current) return
          busy = true
          try {
            const codes = await detector.detect(video.current)
            const hit = codes.find((c) => c.rawValue?.trim())
            if (hit) {
              stop()
              onResult(cleanScan(hit.rawValue))
            }
          } catch {
            // A frame that is not ready yet; keep looking.
          } finally {
            busy = false
          }
        }, 250)
      })
      .catch(() => onClose('Camera unavailable or blocked; type the serial'))
    return stop
  }, [])
  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-border bg-foreground/90">
      <video ref={video} playsInline muted className="aspect-video w-full object-cover" aria-label="Camera preview" />
      <div className="flex items-center justify-between gap-2 bg-card px-3 py-2 text-sm">
        <span className="text-muted-foreground">Point the camera at the serial barcode</span>
        <Button size="sm" variant="ghost" onClick={() => onClose()}>
          <X /> Stop
        </Button>
      </div>
    </div>
  )
}

export function SerialField({
  label,
  value,
  onChange,
  onScanMac,
  autoFocus,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  /** A scan that reads as a MAC address goes here instead of the serial. */
  onScanMac?: (value: string) => void
  autoFocus?: boolean
}) {
  const [scanning, setScanning] = React.useState(false)
  const [note, setNote] = React.useState<string | null>(null)
  const scannable = canScan()
  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          placeholder="Type or scan the serial"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          autoFocus={autoFocus}
          className="h-11 flex-1 font-mono text-base"
        />
        {scannable ? (
          <Button
            variant="outline"
            className="h-11"
            aria-label={`Scan ${label}`}
            onClick={() => {
              setNote(null)
              setScanning(true)
            }}
          >
            <ScanLine /> Scan
          </Button>
        ) : null}
      </div>
      {scanning ? (
        <Scanner
          onResult={(v) => {
            setScanning(false)
            if (onScanMac && looksLikeMac(v)) {
              onScanMac(v)
              setNote(`Read a MAC address (${v}); put it in the MAC field`)
            } else {
              onChange(v)
              setNote(`Scanned ${v}; check it against the label`)
            }
          }}
          onClose={(error) => {
            setScanning(false)
            if (error) setNote(error)
          }}
        />
      ) : null}
      {note ? <p className="mt-1 text-xs text-muted-foreground">{note}</p> : null}
    </div>
  )
}
